import {
    ClipboardText,
    ClipboardTick,
    Clock,
    Danger,
    DocumentText,
    Note1,
    Sparkles,
} from '@/components/icons';
import type { IconVariant } from '@/components/icons';
import type { PatientAnamnesis } from '@/types/patient-anamnesis';
import type {
    PatientReportOverview,
    PatientSurveyOverview,
    PatientTestOverview,
} from '@/types/patient-overview';
import type { ComponentType } from 'react';

/** Tab values owned by `PatientDetails`; a pill click selects one of these. */
export type PatientStatusTab = 'reports' | 'anamnesis' | 'surveys' | 'tests' | 'import-results';

export type PatientStatusTone = 'warning' | 'brand' | 'info' | 'neutral';

export interface PatientStatusItem {
    key: string;
    label: string;
    tone: PatientStatusTone;
    icon: ComponentType<{ className?: string; variant?: IconVariant }>;
    tab: PatientStatusTab;
}

interface DerivePatientStatusInput {
    surveys: PatientSurveyOverview[];
    tests: PatientTestOverview[];
    reports: PatientReportOverview[];
    anamnesis?: PatientAnamnesis | null;
    /** Publisher-result imports still being processed. Always 0 when the feature is off. */
    activeImports?: number;
}

export interface PatientStatus {
    items: PatientStatusItem[];
    lastActivity?: string;
}

const plural = (count: number, singular: string, pluralForm: string) =>
    count === 1 ? singular : pluralForm;

/**
 * The header answers "what do I still owe this patient?", so only states that
 * need an action get a pill — totals already live on the tab badges.
 */
export const derivePatientStatus = ({
    surveys,
    tests,
    reports,
    anamnesis,
    activeImports = 0,
}: DerivePatientStatusInput): PatientStatus => {
    const items: PatientStatusItem[] = [];

    const overdueTests = tests.filter((test) => test.assignment_state === 'overdue').length;
    if (overdueTests > 0) {
        items.push({
            key: 'tests-overdue',
            label: `${overdueTests} ${plural(overdueTests, 'instrumento', 'instrumentos')} com prazo expirado`,
            tone: 'warning',
            icon: Danger,
            tab: 'tests',
        });
    }

    const pendingSurveys = surveys.filter((survey) => survey.status === 'PENDING').length;
    if (pendingSurveys > 0) {
        items.push({
            key: 'surveys-pending',
            label: `${pendingSurveys} ${plural(pendingSurveys, 'formulário pendente', 'formulários pendentes')}`,
            tone: 'warning',
            icon: ClipboardText,
            tab: 'surveys',
        });
    }

    const readyConsolidations = tests.filter(
        (test) => test.consolidation_status === 'ready',
    ).length;
    if (readyConsolidations > 0) {
        items.push({
            key: 'tests-ai-ready',
            label: `${readyConsolidations} ${plural(readyConsolidations, 'resultado de IA pronto', 'resultados de IA prontos')} para revisão`,
            tone: 'brand',
            icon: Sparkles,
            tab: 'tests',
        });
    }

    const failedConsolidations = tests.filter((test) =>
        test.consolidation_status?.startsWith('failed'),
    ).length;
    if (failedConsolidations > 0) {
        items.push({
            key: 'tests-ai-failed',
            label: `${failedConsolidations} ${plural(failedConsolidations, 'resultado de IA com falha', 'resultados de IA com falha')}`,
            tone: 'warning',
            icon: Danger,
            tab: 'tests',
        });
    }

    const awaitingTests = tests.filter(
        (test) => test.assignment_state === 'active' && test.status !== 'completed',
    ).length;
    if (awaitingTests > 0) {
        items.push({
            key: 'tests-awaiting',
            label: `${awaitingTests} ${plural(awaitingTests, 'instrumento aguardando', 'instrumentos aguardando')} resposta`,
            tone: 'info',
            icon: ClipboardTick,
            tab: 'tests',
        });
    }

    if (reports.some((report) => report.status === 'in_progress')) {
        items.push({
            key: 'report-in-progress',
            label: 'Laudo em andamento',
            tone: 'info',
            icon: DocumentText,
            tab: 'reports',
        });
    }

    if (!anamnesis?.source) {
        items.push({
            key: 'anamnesis-missing',
            label: 'Sem anamnese',
            tone: 'neutral',
            icon: Note1,
            tab: 'anamnesis',
        });
    }

    if (activeImports > 0) {
        items.push({
            key: 'imports-active',
            label: `${activeImports} ${plural(activeImports, 'importação em andamento', 'importações em andamento')}`,
            tone: 'info',
            icon: Clock,
            tab: 'import-results',
        });
    }

    return { items, lastActivity: getLastActivity({ surveys, tests, reports }) };
};

const getLastActivity = ({
    surveys,
    tests,
    reports,
}: Pick<DerivePatientStatusInput, 'surveys' | 'tests' | 'reports'>) => {
    const timestamps = [
        ...reports.map((report) => report.updated_at),
        ...surveys.map((survey) => survey.answered_at || survey.created_at),
        ...tests.map((test) => test.answered_at || test.created_at),
    ].filter((value): value is string => Boolean(value));

    if (timestamps.length === 0) return undefined;

    return timestamps.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
};
